/**
 * ToastSystem.js
 * グローバルトースト通知（成功=グリーン / 失敗=レッド / メッセージ=ブルー / その他=グレー）
 *
 * ▼使い方（最小）
 * import { ToastProvider, useToast } from '../common/ToastSystem';
 *
 * // 1) ページ全体を ToastProvider でラップ
 * export default function SomePage() {
 *   return (
 *     <ToastProvider position="top-right">
 *       <SomePageInner />
 *     </ToastProvider>
 *   );
 * }
 *
 * // 2) 子コンポーネントで useToast() を呼び出して表示
 * function SomePageInner() {
 *   const { success, error, info, message, neutral, showToast } = useToast();
 *   // success('保存しました'); error('失敗しました'); info('お知らせ…'); message('新着メッセージ'); neutral('その他');
 *   // showToast({ type: 'success'|'error'|'info'|'message'|'neutral', text: '任意テキスト', duration: 2400 });
 *   ...
 * }
 *
 * App.jsでサービス全体にラップしている為、どのページでも簡単に利用できるように既になっています。
 */

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import '../../css/common/Toast.css';

const ToastContext = createContext(null);

// 種別ごとのアイコン（塗りと同系色。差し色をドットからアイコンへ寄せて peer トーストと家族感を統一）
const TOAST_ICON = {
  success: 'fa-solid fa-circle-check',
  error: 'fa-solid fa-circle-exclamation',
  info: 'fa-solid fa-circle-info',
  message: 'fa-solid fa-comment-dots',
  neutral: 'fa-solid fa-circle-info',
  peer: 'fa-solid fa-bell',
};

export function ToastProvider({ children, position = 'top-right', defaultDuration = 2400 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const globalHandlerRef = useRef(null);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((t) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const toast = { id, ...t };
    setToasts(prev => [...prev, toast]);

    const timer = setTimeout(() => remove(id), t.duration ?? defaultDuration);
    timersRef.current.set(id, timer);
  }, [defaultDuration, remove]);

  const showToast = useCallback(({ type = 'neutral', text = '', duration }) => {
    push({ type, text, duration });
  }, [push]);

  const api = useMemo(() => ({
    showToast,
    success:   (text, duration) => showToast({ type: 'success',   text, duration }),
    error:     (text, duration) => showToast({ type: 'error',     text, duration }),
    info:      (text, duration) => showToast({ type: 'info',      text, duration }),
    message:   (text, duration) => showToast({ type: 'message',   text, duration }),   // 自分の操作に紐づくメッセージ（青）
    neutral:   (text, duration) => showToast({ type: 'neutral',   text, duration }),
    peerEvent: (text, duration) => showToast({ type: 'peer',      text, duration }),   // 相手からのアクション用（サイドバー系カラー）
  }), [showToast]);

  // グローバルイベント経由でのトースト発火（例: fetch ラッパーからの通知）
  React.useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      const { type = 'error', text, duration } = detail;
      if (!text) return;
      showToast({ type, text, duration });
    };
    globalHandlerRef.current = handler;
    window.addEventListener('app:toast', handler);
    return () => {
      window.removeEventListener('app:toast', handler);
      globalHandlerRef.current = null;
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={`tc-toast-root ${position}`} aria-live="polite" aria-atomic="true">
        {toasts.map(t => (
          <div key={t.id} className={`tc-toast ${t.type}`}>
            <i className={`tc-icon ${TOAST_ICON[t.type] || TOAST_ICON.neutral}`} aria-hidden="true" />
            <span className="tc-text">{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
